// Copyright (c) StrangeBee 2023

import React, { useContext, ReactElement } from 'react';
import { CustomFieldValueOutput, CaseCustomField } from 'thehive-sdk';
import { FormListFieldData } from 'antd/lib/form/FormList';
import { CFValueItem } from '@Molecules/CFList/CFValueItem';
import { ICONS } from '@Constants/iconsApp';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { useLocalMessage } from '@Helpers/locale';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';
import { ScopeContext } from '@Context/ScopeProvider';

import './CFListItem.scss';
import { MandatoryAsterisk } from '@Atoms/MandatoryAsterisk/MandatoryAsterisk';

interface CFListItemProps {
    name: string;
    onAdd: (customField: Partial<CaseCustomField>) => void;
    customFields: (CustomFieldValueOutput & {
        field: FormListFieldData;
    })[];
    onDelete?: (fieldName: number) => void;
    borderless?: boolean;
    disabled?: boolean;
}

export const CFListItem = ({
    name,
    onAdd,
    customFields,
    onDelete,
    borderless,
    disabled,
}: CFListItemProps): ReactElement => {
    const i = useLocalMessage();

    const { customFieldList: customFieldsInfos } = useContext(ScopeContext);

    const cf = customFieldsInfos?.find(({ name: cfName }) => cfName === name);

    const { displayName, description, type, mandatory } = cf || {};

    return (
        <div className="CfListItem" key={name}>
            <div className="CfListItem__Name">
                <span>{displayName || name}</span>
                {mandatory && <MandatoryAsterisk />}
                <THTooltip title={description}>
                    <ICONS.QuestionCircleOutlined className="CfListItem__AddDescriptionIcon" />
                </THTooltip>
                {type !== 'boolean' && !disabled && (
                    <THButton className="CfListItem__AddButton" type="link" onClick={() => cf && onAdd(cf)}>
                        {i('general.add')}
                    </THButton>
                )}
            </div>

            {customFields?.map(({ field, ...customField }, cfIndex) => (
                <CFValueItem
                    showLabel={false}
                    key={name + cfIndex}
                    fieldName={field?.name}
                    customField={customField}
                    onDelete={
                        onDelete
                            ? () => {
                                  onDelete?.(field?.name);
                              }
                            : undefined
                    }
                    borderless={borderless}
                    disabled={disabled}
                />
            ))}
        </div>
    );
};
